import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import {
  CalendarOptions,
  DateSelectArg,
  DatesSetArg,
  DayHeaderContentArg,
  EventApi,
  EventClickArg,
  EventDropArg,
  EventInput,
  MoreLinkContentArg,
} from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import { DateClickArg, Draggable } from '@fullcalendar/interaction';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { Item, TreeDayRangeSelectionStrategy } from '../form';
import { SmartModalService } from '../smart-modal';
import { CalendarHeaderButtonsController } from './calendar-header-buttons.controller';
import {
  buildBaseCalendarOptions,
  buildToolbarButtons,
  MachinerySlotPreview,
} from './calendar-options.builder';
import {
  calculateEndDate,
  CLICK_SNAP_MINUTES,
  hasEsitoRegistrato as computeHasEsitoRegistrato,
  isAppuntamentoUnresolved as computeIsAppuntamentoUnresolved,
  isAssenzaEvent as computeIsAssenzaEvent,
  isEventInPast as computeIsEventInPast,
  isPromemoriaEvent as computeIsPromemoriaEvent,
  isTravelEvent as computeIsTravelEvent,
  isTurnoEvent as computeIsTurnoEvent,
  EVENT_MIN_VISUAL_MINUTES,
  formatAllDayDateRange,
  formatDateAwareTimeRange,
  formatPromemoriaAllDayDeadline,
  formatTime,
  formatTurnoTimeRange,
  getMoreLinkLabel,
  renderDayHeaderHtml,
  roundDownToQuarterHour,
  toMonthViewEvents,
} from './calendar.utils';
import { EventPopoverTooltipComponent } from './components/event-popover.component';
import { QuickAddMenuComponent } from './components/quick-add-menu.component';
import { ToolTipRendererDirective } from './directives/event-popover.directive';
import {
  CalendarMode,
  CalendarView,
  CustomCalendarOptions,
  ManageEventAction,
  QuickAddMenuOption,
  SlotQuickAddEvent,
} from './models/calendar.model';
import { HolidaysService } from './services/holidays.service';
import { MachineryService } from './services/machinery.service';

@UntilDestroy()
@Component({
  imports: [
    MatButtonModule,
    NgClass,
    MatCardModule,
    MatIconModule,
    MatMenuModule,
    MatTooltip,
    DragDropModule,
    ToolTipRendererDirective,
    FullCalendarModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    FormsModule,
    DatePipe,
    TitleCasePipe,
    QuickAddMenuComponent,
  ],
  selector: 'siat-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: TreeDayRangeSelectionStrategy,
    },
    provideNativeDateAdapter(),
  ],
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Services.
   */
  readonly translateService = inject(TranslateService);
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly dialog = inject(SmartModalService);
  readonly renderer = inject(Renderer2);
  readonly holidaysService = inject(HolidaysService);
  private readonly machineryService = inject(MachineryService);
  private readonly injector = inject(Injector);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly headerButtons = new CalendarHeaderButtonsController({
    viewContainerRef: this.viewContainerRef,
    renderer: this.renderer,
    onSplitButtonAction: value => this.splitButtonAction.emit(value),
  });

  /**
   * Initial start and end dates.
   */
  private readonly initialStartDate = new Date();
  private readonly initialEndDate = calculateEndDate(this.initialStartDate);

  /**
   * The formgroup for date range view.
   */
  readonly range = new FormGroup({
    start: new FormControl<Date | null>(this.initialStartDate),
    end: new FormControl<Date | null>(this.initialEndDate),
  });

  /**
   * Required viewchild
   */
  calendar = viewChild<FullCalendarComponent>('calendar');

  /**
   * Template refs for header buttons.
   */
  customTooltipTemplate = viewChild<TemplateRef<EventPopoverTooltipComponent>>(
    'customTooltipTemplate'
  );

  changeViewButton = viewChild.required<TemplateRef<unknown>>(
    'viewButtonTemplate'
  );
  dateRangePicker = viewChild.required<TemplateRef<unknown>>(
    'dateRangeTemplate'
  );
  addPrestazioneButton = viewChild.required<TemplateRef<unknown>>(
    'addPrestazioneTemplate'
  );

  /**
   * The available views.
   */
  readonly availableViews: CalendarView[] = [
    { type: 'timeGridWeek', label: 'Settimana' },
    { type: 'dayGridMonth', label: 'Mese' },
    { type: 'timeGridDay', label: 'Giorno' },
  ];

  /**
   * Input events.
   */
  readonly events = input<EventInput[]>();

  /**
   * True if `events()` contains at least one event flagged as belonging to
   * "la prestazione corrente" (`hasMatchingPrestazione`, set by
   * `mapApiToCalendarEvents` when the consumer passes an `idPrestazione`).
   * Gates the "Focus Prestazione" toggle — pages with no such notion (e.g.
   * the standalone calendario page) never show it.
   */
  readonly hasPrestazioneEvents = computed<boolean>(() =>
    (this.events() ?? []).some(
      e =>
        !!(e.extendedProps as Record<string, unknown> | undefined)?.[
          'hasMatchingPrestazione'
        ]
    )
  );

  /**
   * True while "Focus Prestazione" is active: events NOT belonging to the
   * current prestazione are dimmed (see `.event-defocused` in the SCSS and
   * the `eventContent` template).
   */
  readonly focusPrestazione = signal<boolean>(false);

  /**
   * Toggles "Focus Prestazione" (see `focusPrestazione`).
   */
  toggleFocusPrestazione(): void {
    this.focusPrestazione.update(value => !value);
  }

  /**
   * Explicitly sets "Focus Prestazione" (see `focusPrestazione`), e.g. when
   * "Vai al giorno" should turn it on rather than just toggling it.
   */
  setFocusPrestazione(value: boolean): void {
    this.focusPrestazione.set(value);
  }

  /**
   * Optional extra template to inject into the calendar header (e.g. toggle buttons).
   */
  readonly extraHeaderTemplate = input<TemplateRef<unknown>>();

  /**
   * The current calendar context (sanitario/ambulatorio).
   */
  readonly calendarContext = input<'SANITARIO' | 'AMBULATORIO'>('SANITARIO');

  /**
   * The current calendar mode (sanitario/ambulatorio). Shows a quick-add
   */
  readonly mode = input<CalendarMode>('sanitario');

  /**
   * Store for operator event types
   */
  readonly operatorEventTypeItems = input<Item[]>([]);

  /**
   * The current view.
   */
  readonly currentView = signal<CalendarView>(this.availableViews[0]);

  /**
   * The current title.
   */
  readonly currentTitle = signal<string>('');

  /**
   * Input calendar options from parent.
   */
  customOptions = input<CustomCalendarOptions>();

  /**
   * Options for the split button (e.g. ['Assenza'] or ['Nuovo turno']).
   */
  readonly splitButtonOptions = input<string[]>(['Assenza']);

  /**
   * If true, show holidays in the calendar.
   */
  readonly showHolidays = input<boolean>(false);

  /**
   * Decides if the hover tooltip shows the "Elimina" quick action for an appuntamento.
   */
  readonly canDeleteEvent = input<(event: EventApi) => boolean>(() => false);

  /**
   * The city of the holidays.
   */
  readonly holidayCitta = input<string>('TRENTO');

  /**
   * Set to true to block holiday insertion.
   */
  readonly blockHolidayInsert = input<boolean>(false);

  /**
   * Set to true to skip external draggable events.
   */
  readonly skipExternalDraggable = input<boolean>(false);

  /** When true, the machinery slot preview only shows while `isDraggingActivity` is true. */
  readonly requireDragForMachineryPreview = input<boolean>(false);

  /** True while an external activity card is being dragged over the calendar. */
  readonly isDraggingActivity = input<boolean>(false);

  /** True when the machinery hover preview should be tracked/shown. */
  readonly machineryPreviewEnabled = computed(
    () => !this.requireDragForMachineryPreview() || this.isDraggingActivity()
  );

  /**
   * The final, merged calendar options.
   */
  readonly calendarOptions = signal<CalendarOptions | null>(null);

  /** The machinery slot currently previewed under the cursor, if any. */
  readonly hoveredMachinerySlot = signal<MachinerySlotPreview | null>(null);

  /** The event currently previewed under the cursor, if any. */
  private readonly hoveredEventId = signal<string | null>(null);

  /**
   * Outputs.
   */
  onClickEvent = output<EventImpl>();
  addPrestazioneClicked = output<DateClickArg | null>();
  markAsNoShowEvent = output<EventImpl>();
  splitButtonAction = output<string>();
  deleteAssenzaClicked = output<string>();
  quickAddAction = output<SlotQuickAddEvent>();
  manageEventAction = output<ManageEventAction>();
  visibleRangeChange = output<{ start: Date; end: Date }>();

  /**
   * Constants.
   */
  readonly DURATION_PRESTAZIONE_IN_MS = input<number>(60);

  /**
   * Quick-add menu
   */
  private readonly quickAddMenu = viewChild(QuickAddMenuComponent);

  private pendingQuickAddRange: { start: Date; end: Date } | null = null;

  /**
   * Quick-add menu options
   */
  protected readonly quickAddMenuOptions = computed<QuickAddMenuOption[]>(
    () => {
      if (this.mode() === 'ambulatorio') {
        return [{ action: 'Nuovo turno', icon: '🏢', label: 'Nuovo Turno' }];
      }
      const items = this.operatorEventTypeItems();
      if (!items.length) {
        return [
          { action: 'Assenza', icon: '🚫', label: 'Assenza' },
          { action: 'Riunione', icon: '👥', label: 'Riunione' },
          { action: 'Promemoria', icon: '🔔', label: 'Promemoria' },
        ];
      }
      return items.map(item => ({
        action: item.name,
        icon: item.icon ?? '📌',
        label: item.name,
      }));
    }
  );

  /**
   * Keeps the header's split-button options in sync with `splitButtonOptions`.
   */
  private readonly syncHeaderSplitButtonOptions = effect(() => {
    this.headerButtons.syncSplitButtonOptions(this.splitButtonOptions());
  });

  /**
   * Keeps the holidays service's insert-block flag in sync with the input.
   */
  private readonly syncHolidayInsertBlock = effect(() => {
    this.holidaysService.blockHolidayInsert.set(this.blockHolidayInsert());
  });

  /** FullCalendar's currently active view type, kept in sync from `datesSet`. */
  private readonly activeViewType = signal<string>('timeGridWeek');

  /**
   * Keeps the calendar's event sources in sync with `events`.
   */
  private readonly syncEventSources = effect(() => {
    const calendarApi = this.calendar()?.getApi();
    const apiEventsData = this.events();
    const isMonthView = this.activeViewType() === 'dayGridMonth';

    if (calendarApi && apiEventsData) {
      calendarApi.removeAllEventSources();
      const visibleEvents = isMonthView
        ? toMonthViewEvents(apiEventsData)
        : apiEventsData;
      const assenzaEvents = visibleEvents.filter(computeIsAssenzaEvent);
      const otherEvents = visibleEvents.filter(e => !computeIsAssenzaEvent(e));
      calendarApi.addEventSource([...assenzaEvents, ...otherEvents]);
      this.updateAllDaySlotVisibility();
    }
  });

  /**
   * Rebuilds `calendarOptions` whenever `customOptions()`/`extraHeaderTemplate()` change.
   */
  private readonly syncCalendarOptions = effect(() => {
    const custom = this.customOptions() || {};
    const {
      changeViewButton = false,
      addPrestazioneButton = false,
      dateRangeSelection = false,
      addNewEventButton = false,
      showTooltip: _showTooltip = false,
      nonClickableEventTypes: _nonClickableEventTypes,
      ...restCustomOptions
    } = custom;

    const { endToolbarButtons, customButtons } = buildToolbarButtons({
      addNewEventButton,
      changeViewButton,
      hasExtraHeaderTemplate: !!this.extraHeaderTemplate(),
      addPrestazioneButton,
      dateRangeSelection,
    });

    const baseOptions: Partial<CalendarOptions> = buildBaseCalendarOptions({
      translateLang: this.translateService.getCurrentLang(),
      isDateAllowed: date => this.holidaysService.isDateAllowed(date),
      hasBackgroundEventAt: date => this.hasBackgroundEventCoveringDate(date),
      isHoliday: date => this.holidaysService.isHoliday(date),
      endToolbarButtons,
      customButtons,
      onViewReady: () => this.onViewReady(),
      renderCustomDayHeader: props => this.renderCustomDayHeader(props),
      onDatesSet: info => this.handleDatesSet(info),
      snapMachinerySlot: (date, macchinario) =>
        this.machineryService.snapToSlot(date, macchinario),
      hasMachinerySlotConflict: (idMacchinario, start, end) =>
        this.machineryService.hasBookingConflict(
          idMacchinario,
          start,
          end,
          this.events() ?? []
        ),
      isMachineryPreviewEnabled: () => this.machineryPreviewEnabled(),
      onMachineryHover: preview => this.hoveredMachinerySlot.set(preview),
      onEventHoverChange: id => this.hoveredEventId.set(id),
    });

    const defaultHandlers: Partial<CalendarOptions> = {
      dateClick: arg => this.handleDateClick(arg),
      eventClick: arg => this.handleEventClick(arg),
      eventDrop: info => this.handleEventDrop(info),
    };

    if (this.customOptions().selectable) {
      defaultHandlers.select = arg => this.handleSlotSelect(arg);
      baseOptions.unselectAuto = true;
    }

    if (!addNewEventButton) {
      this.headerButtons.removeAddNewEventButtonComponents();
    }

    if (
      !addPrestazioneButton &&
      this.headerButtons.hasAddPrestazioneButtonContainer
    ) {
      this.headerButtons.clearAddPrestazioneButtonContainer();
    }

    if (!this.extraHeaderTemplate()) {
      this.headerButtons.removeExtraHeaderView();
    }

    this.calendarOptions.set({
      ...baseOptions,
      ...defaultHandlers,
      ...restCustomOptions,
    });

    afterNextRender(() => this.injectCustomHeaderComponents(), {
      injector: this.injector,
    });
  });

  /**
   * Trova l'evento "background" (turno/assenza/travel time) che copre la
   * data/ora indicata, in base ai dati reali dell'evento (non alla posizione
   * dei pixel: un hit-test sul DOM `elementsFromPoint` può fallire
   * silenziosamente a seconda di come l'evento viene renderizzato).
   */
  private findBackgroundEventAt(date: Date): EventImpl | undefined {
    const isWeekView = this.calendar()?.getApi()?.view?.type === 'timeGridWeek';
    const candidates =
      this.calendar()
        ?.getApi()
        ?.getEvents()
        .filter(e => {
          if (
            e.display !== 'background' ||
            e.groupId === 'disponibilita-macchinario' ||
            !e.start
          ) {
            return false;
          }
          const realEnd = e.end ?? e.start;
          if (!isWeekView) {
            return e.start <= date && date <= realEnd;
          }

          const minVisualEnd = new Date(
            e.start.getTime() + EVENT_MIN_VISUAL_MINUTES * 60000
          );
          const end = realEnd > minVisualEnd ? realEnd : minVisualEnd;
          const bucketEnd = new Date(
            date.getTime() + CLICK_SNAP_MINUTES * 60000
          );
          return e.start <= bucketEnd && date <= end;
        }) ?? [];

    const hoveredId = this.hoveredEventId();
    const hovered = hoveredId && candidates.find(e => e.id === hoveredId);
    return hovered || candidates[0];
  }

  /**
   * @returns { boolean }
   * true if there is an event that covers the given date
   */
  private hasBackgroundEventCoveringDate(date: Date): boolean {
    return !!this.findBackgroundEventAt(date);
  }

  /**
   * Chiamato quando l'utente seleziona uno slot
   */
  private handleSlotSelect(selectInfo: DateSelectArg): void {
    const calendarApi = this.calendar().getApi();
    const clickedEvent = this.findBackgroundEventAt(selectInfo.start);

    if (clickedEvent) {
      calendarApi.unselect();

      const scrollPositions = this.pendingBackgroundEventScrollPositions;
      this.pendingBackgroundEventScrollPositions = null;

      if (scrollPositions) {
        requestAnimationFrame(() => {
          scrollPositions.forEach(({ el, scrollTop }) => {
            el.scrollTop = scrollTop;
          });
        });
      }

      this.onClickEvent.emit(clickedEvent);
      return;
    }

    const startTime = selectInfo.start;
    if (!this.holidaysService.isDateAllowed(startTime)) return;

    calendarApi.unselect();

    if (this.mode() === 'segnalazione') return;
    this.openQuickAddMenu(selectInfo);
  }

  /**
   * If the user clicks on a background event, we need to remember the scroll
   * position of the view so we can restore it after the event is selected
   */
  private pendingBackgroundEventScrollPositions: Array<{
    el: HTMLElement;
    scrollTop: number;
  }> | null = null;

  /**
   * On potential background event mousedown
   */
  private handlePotentialBackgroundEventMousedown = (ev: MouseEvent): void => {
    const target = ev.target as HTMLElement | null;
    if (!target?.closest('.fc-bg-event .event-content-wrapper')) {
      return;
    }
    this.pendingBackgroundEventScrollPositions = Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
        '.fc-scroller'
      )
    ).map(el => ({ el, scrollTop: el.scrollTop }));
  };

  /**
   * Handle the scroll event
   */
  private handleCalendarScroll = (): void => {
    this.elementRef.nativeElement
      .querySelectorAll<HTMLElement>('.fc-event-hovered')
      .forEach(el => {
        el.style.zIndex = '';
        el.classList.remove('fc-event-hovered');
      });
    this.hoveredEventId.set(null);
  };

  /**
   * Open the quick-add menu for the selected slot
   */
  private openQuickAddMenu(selectInfo: DateSelectArg): void {
    const jsEvent = selectInfo.jsEvent;
    if (!jsEvent) return;
    this.pendingQuickAddRange = {
      start: selectInfo.start,
      end: selectInfo.end,
    };
    this.quickAddMenu()?.open({ x: jsEvent.clientX, y: jsEvent.clientY });
  }

  /**
   * Handles a quick-add option click
   */
  protected onQuickAddOptionClick(action: string): void {
    const range = this.pendingQuickAddRange;
    this.pendingQuickAddRange = null;
    if (!range) return;
    this.quickAddAction.emit({ action, start: range.start, end: range.end });
  }

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.listenForLanguageChanges();
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit(): void {
    if (!this.skipExternalDraggable()) {
      this.initializeExternalDraggables();
    }
    this.elementRef.nativeElement.addEventListener(
      'mousedown',
      this.handlePotentialBackgroundEventMousedown,
      true
    );
    this.elementRef.nativeElement.addEventListener(
      'scroll',
      this.handleCalendarScroll,
      true
    );

    this.containerResizeObserver = new ResizeObserver(() => {
      this.calendar()?.getApi()?.updateSize();
    });
    this.containerResizeObserver.observe(this.elementRef.nativeElement);
  }

  /**
   * Watches the host element's size to keep FullCalendar's layout in sync
   * with container-driven resizes (see `ngAfterViewInit`).
   */
  private containerResizeObserver?: ResizeObserver;

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener(
      'mousedown',
      this.handlePotentialBackgroundEventMousedown,
      true
    );
    this.elementRef.nativeElement.removeEventListener(
      'scroll',
      this.handleCalendarScroll,
      true
    );
    this.containerResizeObserver?.disconnect();
  }

  /**
   * Sets the calendar view.
   */
  public setView(selectedView: CalendarView): void {
    this.calendar()?.getApi()?.changeView(selectedView.type);
    this.currentView.set(selectedView);
    if (selectedView.type === 'threeDay') {
      this.headerButtons.injectDateRangePicker(this.dateRangePicker() ?? null);
    }
  }

  /**
   * Goes to the previous period.
   */
  public prev(): void {
    this.calendar()?.getApi()?.prev();
  }

  /**
   * Goes to the next period
   */
  public next(): void {
    this.calendar()?.getApi()?.next();
  }

  /**
   * Goes to the current period
   */
  public goToday(): void {
    this.calendar()?.getApi()?.today();
  }

  /**
   * Forces FullCalendar to re-measure its container and recompute its
   * internal layout. FullCalendar only reacts automatically to `window`
   * resize events, not to its own container changing size (e.g. a sidenav
   * pushing/resizing it), so callers must trigger this explicitly after such
   * a layout change.
   */
  public refreshSize(): void {
    this.calendar()?.getApi()?.updateSize();
  }

  /**
   * Goes to a specific date and, on time-grid views, scrolls so the slot
   * matching `date`'s time is centered in the visible scroller (FullCalendar's
   * own `scrollToTime` only top-aligns it).
   */
  public gotoDate(date: Date): void {
    this.calendar()?.getApi()?.gotoDate(date);
    this.centerTimeSlotInView(date);
  }

  /**
   * Scrolls the time-grid scroller so the slot matching `date`'s time sits in
   * the middle of the viewport. No-op on views without a time grid (e.g.
   * month view). Deferred across two frames because FullCalendar re-renders
   * the grid asynchronously after `gotoDate`.
   */
  private centerTimeSlotInView(date: Date): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const roundedMinutes = date.getMinutes() < 30 ? '00' : '30';
        const slotTime = `${date.getHours().toString().padStart(2, '0')}:${roundedMinutes}:00`;
        const slotEl = this.elementRef.nativeElement.querySelector<HTMLElement>(
          `.fc-timegrid-slot[data-time="${slotTime}"]`
        );
        const scroller = slotEl?.closest<HTMLElement>('.fc-scroller');
        if (!slotEl || !scroller) return;
        scroller.scrollTop =
          slotEl.offsetTop -
          scroller.clientHeight / 2 +
          slotEl.offsetHeight / 2;
      });
    });
  }

  /**
   * Inject the custom component into FullCalendar header.
   */
  private onViewReady(): void {
    this.injectCustomHeaderComponents();
    this.fixAxisHeaderA11y();
    this.fixNavIconsA11y();
    this.fixColHeaderTableA11y();
  }

  /**
   * Fixes the accessibility of the axis header
   */
  private fixAxisHeaderA11y(): void {
    const axisHeader = document.querySelector('th.fc-timegrid-axis');
    if (!axisHeader) return;
    this.renderer.setAttribute(axisHeader, 'aria-label', 'Orario');
    this.renderer.setAttribute(axisHeader, 'scope', 'col');
  }

  /**
   * Fixes the accessibility of the navigation icons
   */
  private fixNavIconsA11y(): void {
    const prevIcon = document.querySelector('.fc-prev-button .fc-icon');
    const nextIcon = document.querySelector('.fc-next-button .fc-icon');
    if (prevIcon)
      this.renderer.setAttribute(prevIcon, 'aria-label', 'Periodo precedente');
    if (nextIcon)
      this.renderer.setAttribute(nextIcon, 'aria-label', 'Periodo successivo');
  }

  /**
   * Fixes the accessibility of the column header table
   */
  private fixColHeaderTableA11y(): void {
    const colHeaderTable = document.querySelector('table.fc-col-header');
    if (!colHeaderTable) return;
    this.renderer.removeAttribute(colHeaderTable, 'role');
    colHeaderTable.querySelectorAll('th.fc-col-header-cell').forEach(th => {
      this.renderer.setAttribute(th, 'scope', 'col');
    });
  }

  /**
   * Handle date or times click on calendar (DEFAULT BEHAVIOR).
   * Open a MatDialog.
   * @param arg The event click data by FullCalendar.
   */
  private handleDateClick(arg: DateClickArg): void {
    if (!this.holidaysService.isDateAllowed(arg.date)) return;
  }

  /**
   * Handle event click on calendar (DEFAULT BEHAVIOR).
   * Open a MatDialog.
   * @param arg The event click data by FullCalendar.
   */
  private handleEventClick(arg: EventClickArg): void {
    if (arg.event.groupId === 'disponibilita-macchinario') return;
    if (arg.event.display === 'background') return;
    this.onClickEvent.emit(arg?.event);
  }

  /**
   * Handle event drop on calendar (DEFAULT BEHAVIOR).
   * @param info The event drop data by FullCalendar.
   */
  handleEventDrop(info: EventDropArg): void {
    if (!this.holidaysService.isDateAllowed(info.event.start)) {
      info.revert();
      return;
    }
    const originalDuration =
      info.oldEvent.end.getTime() - info.oldEvent.start.getTime();
    const newStart = roundDownToQuarterHour(new Date(info.event.start));
    const newEnd = new Date(newStart.getTime() + originalDuration);

    info.event.setStart(newStart);
    info.event.setEnd(newEnd);
  }

  /**
   * Inizialize external drag function.
   */
  private initializeExternalDraggables(): void {
    const availableActivities = document.getElementsByClassName(
      'activity'
    ) as HTMLCollection;
    if (!availableActivities) return;
    const container = availableActivities[0]?.parentElement as HTMLElement;
    if (!container) return;
    new Draggable(container, {
      itemSelector: '.activity',
      eventData: (element: HTMLElement) => {
        const activityId = element?.id;
        const activity = this.events().find(
          act => act.id.toString() === activityId
        );
        const props = activity?.extendedProps;
        if (!activity) return { create: false };

        return {
          duration: '02:00',
          create: false,
          backgroundColor: props['stato'] === 'svolto' ? '#8d4a5a' : '#f5e4e6',
          borderColor: props['stato'] === 'svolto' ? '#8d4a5a' : '#f5e4e6',
          textColor: props['stato'] === 'svolto' ? '#faeeef' : '#201a1b',
          title: props['nomePaziente'],
          description: props['titolo'],
          extendedProps: { ...activity },
          classNames: ['pl-2'],
          eventDisplay: 'block',
        };
      },
    });
  }

  /**
   * Render custom HTML into day header.
   */
  private renderCustomDayHeader(props: DayHeaderContentArg): { html: string } {
    return renderDayHeaderHtml(
      props.date,
      this.translateService.getCurrentLang(),
      this.holidaysService.isHoliday(props.date)
    );
  }

  /**
   * Subscribe to language change event.
   */
  private listenForLanguageChanges(): void {
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.calendar()
          .getApi()
          .setOption('locale', this.translateService.getCurrentLang());
        this.calendar()
          .getApi()
          .setOption(
            'moreLinkContent',
            (arg: MoreLinkContentArg) =>
              `+${arg.num} ${getMoreLinkLabel(this.translateService.getCurrentLang())}`
          );
      });
  }

  /**
   * A private method call for inject all component into header.
   */
  private injectCustomHeaderComponents(): void {
    const flags = this.customOptions() || {};

    this.headerButtons.injectAll({
      extraHeaderTemplate: this.extraHeaderTemplate() ?? null,
      addPrestazioneButton: !!flags.addPrestazioneButton,
      addPrestazioneTemplate: this.addPrestazioneButton() ?? null,
      addNewEventButton: !!flags.addNewEventButton,
      splitButtonOptions: this.splitButtonOptions(),
      changeViewButton: !!flags.changeViewButton,
      changeViewTemplate: this.changeViewButton() ?? null,
    });
  }

  /**
   *  Handle dates set on calendar (DEFAULT BEHAVIOR).
   */
  private handleDatesSet(info: DatesSetArg): void {
    this.activeViewType.set(info.view.type);
    this.updateAllDaySlotVisibility();
    this.currentTitle.set(info.view.title);
    this.visibleRangeChange.emit({ start: info.start, end: info.end });

    if (!this.showHolidays()) return;
    const startYear = info.start.getFullYear();
    // info.end è esclusivo in FullCalendar: sottrai 1ms per avere l'ultimo giorno visibile
    const endYear = new Date(info.end.getTime() - 1).getFullYear();
    [startYear, endYear]
      .filter((y, i, arr) => arr.indexOf(y) === i) // dedup se stesso anno
      .filter(y => !this.holidaysService.isYearLoaded(y))
      .forEach(year => {
        this.holidaysService.markYearLoaded(year);
        this.fetchHolidays(year);
      });
  }

  /**
   * Set allDaySlot option to true if there are visible allDay events.
   */
  private updateAllDaySlotVisibility(): void {
    const calendarApi = this.calendar()?.getApi();
    const view = calendarApi?.view;
    if (!calendarApi || !view) return;

    const hasVisibleAllDayEvent = calendarApi
      .getEvents()
      .some(
        e =>
          e.allDay &&
          e.start < view.activeEnd &&
          (!e.end || e.end > view.activeStart)
      );

    calendarApi.setOption('allDaySlot', hasVisibleAllDayEvent);
  }

  /**
   * Fetch holidays for the given year.
   */
  private fetchHolidays(year: number): void {
    this.holidaysService
      .getHolidays(year, this.holidayCitta())
      .pipe(take(1))
      .subscribe(dates => {
        const calendarApi = this.calendar()?.getApi();
        if (!calendarApi) return;
        this.holidaysService.addHolidays(dates);
        calendarApi.render();
      });
  }

  /**
   * Go to date on select range.
   */
  goToDate(): void {
    const { start } = this.range?.value || {};
    const calendar = this.calendar().getApi();
    if (!start) return; // Aggiunto controllo
    calendar.gotoDate(start);
  }

  /**
   * @returns true if the event is in the past.
   */
  public isEventInPast(event: EventApi): boolean {
    return computeIsEventInPast(event);
  }

  /**
   * True if a past appuntamento has no outcome recorded yet (see `isAppuntamentoUnresolved`).
   * @returns {boolean}
   *  */
  public isAppuntamentoUnresolved(event: EventApi): boolean {
    return computeIsAppuntamentoUnresolved(event);
  }

  /**
   * True if the event already has an outcome registered (`stato` or
   * `dataInizioErogazione`) — used to hide the "Pianificazione"/"Erogazione"
   * quick actions in the hover tooltip once there's nothing left to do.
   * @returns {boolean}
   */
  public hasEsitoRegistrato(event: EventApi): boolean {
    return computeHasEsitoRegistrato(event);
  }

  /**
   * Format an event's start and end time.
   * @returns {string}
   */
  public formatEventTimeRange(event: EventApi): string {
    const realStart = event.extendedProps?.['realStart'];
    const realEnd = event.extendedProps?.['realEnd'];
    const start = realStart ? new Date(realStart) : event.start;
    const end = realEnd ? new Date(realEnd) : event.end;
    if (!start || !end) return '';
    return formatDateAwareTimeRange(start, end);
  }

  /**
   * Format a turno's start and end time.
   * @returns {string}
   */
  public getTurnoTimeRange(event: EventApi): string {
    const realStart = event.extendedProps?.['realStart'];
    const realEnd = event.extendedProps?.['realEnd'];
    const start = realStart ? new Date(realStart) : event.start;
    const end = realEnd ? new Date(realEnd) : event.end;
    if (!start || !end) return '';
    return formatTurnoTimeRange(start, end);
  }

  /**
   * Get the turno title
   * @returns {string}
   */
  public getTurnoTitle(event: EventApi): string {
    if (this.calendarContext() === 'AMBULATORIO') {
      return event.extendedProps?.['descrizione'] || event.title;
    }
    return event.title;
  }

  /**
   * Get the turno context icon
   * @returns {string}
   */
  public getTurnoContextIcon(): string {
    return this.calendarContext() === 'SANITARIO' ? '🏢' : '👤';
  }

  /**
   * Get the allDay summary
   * @returns {string}
   */
  public getAllDaySummary(event: EventApi): string {
    const isAssenza = computeIsAssenzaEvent(event);
    const isPromemoria = computeIsPromemoriaEvent(event);
    const isTurno = computeIsTurnoEvent(event);

    if (isPromemoria) {
      const realStart = event.extendedProps?.['realStart'];
      const realEnd = event.extendedProps?.['realEnd'];
      const start = realStart ? new Date(realStart) : event.start;
      const end = realEnd ? new Date(realEnd) : event.end;
      const deadline =
        start && end ? formatPromemoriaAllDayDeadline(start, end) : '';
      return `🔔 ${event.title} ${deadline}`.trim();
    }

    if (isTurno || isAssenza) {
      const realStart = event.extendedProps?.['realStart'];
      const realEnd = event.extendedProps?.['realEnd'];
      const start = realStart ? new Date(realStart) : event.start;
      const end = realEnd ? new Date(realEnd) : event.end;
      const dateRange = start && end ? formatAllDayDateRange(start, end) : '';
      return isTurno
        ? `${this.getTurnoContextIcon()} - Turno: ${dateRange}`
        : `🚫 Assenza: ${dateRange}`;
    }

    const descrizione = event.extendedProps?.['descrizione'] || event.title;
    return `📌 - ${descrizione}`;
  }

  /**
   * Get the month-view card's time
   * @returns {string}
   */
  public getMonthCardTime(event: EventApi): string {
    return event.start ? formatTime(event.start) : '';
  }

  /**
   * Get the month-view card's icon
   * @returns {string}
   */
  public getMonthCardIcon(event: EventApi): string {
    const tipoNome = (
      event.extendedProps?.['tipoEvento'] as { nome?: string } | undefined
    )?.nome?.toLowerCase();
    if (computeIsAssenzaEvent(event)) return '🚫';
    if (computeIsTurnoEvent(event)) return this.getTurnoContextIcon();
    if (tipoNome === 'riunione') return '👥';
    if (computeIsPromemoriaEvent(event)) return '🔔';
    if (this.hasMachinery(event)) return '⚙️';
    return '🩺';
  }

  /**
   * True if `event` is a real machine-bound booking
   * @returns {boolean}
   */
  public hasMachinery(event: EventApi): boolean {
    return this.machineryService.hasValidId(
      event.extendedProps?.['idMacchinario']
    );
  }

  /**
   * True if `event` is an "assenza".
   */
  public isAssenzaEvent(event: EventApi): boolean {
    return computeIsAssenzaEvent(event);
  }

  /**
   * True if `event` is a "turno".
   */
  public isTurnoEvent(event: EventApi): boolean {
    return computeIsTurnoEvent(event);
  }

  /**
   * True if `event` is a "promemoria".
   */
  public isPromemoriaEvent(event: EventApi): boolean {
    return computeIsPromemoriaEvent(event);
  }

  /**
   * True if `event` is a "travel time".
   */
  public isTravelEvent(event: EventApi): boolean {
    return computeIsTravelEvent(event);
  }

  /**
   * Get the month-view card's secondary line
   * @returns {string}
   */
  public getMonthCardSecondaryLine(event: EventApi): string {
    return (
      event.extendedProps?.['nomePaziente'] ||
      event.extendedProps?.['descrizione'] ||
      ''
    );
  }

  /**
   * Get the month-view card's bottom line
   * @returns {string}
   */
  public getEventCardBottomLine(event: EventApi): string {
    const tipoEvento = event.extendedProps?.['tipoEvento'] as
      { id?: number; nome?: string } | undefined;
    const tipoNome = tipoEvento?.nome?.toLowerCase();
    if (tipoNome === 'promemoria') {
      return event.extendedProps?.['descrizione'] || event.title;
    }
    return event.title;
  }

  /**
   * Get the macchinario card's bottom line
   * @returns {string}
   */
  public getMacchinarioCardBottomLine(event: EventApi): string {
    const nomePrestazione = event.extendedProps?.['nomePrestazione'];
    const nomePaziente = event.extendedProps?.['nomePaziente'];
    return [nomePrestazione, nomePaziente].filter(Boolean).join(' - ');
  }

  /**
   * Emits the absence event ID to the parent for deletion.
   */
  onDeleteAssenza(eventId: string): void {
    this.deleteAssenzaClicked.emit(eventId);
  }

  /**
   * Handle event click on "Segna come non presentato".
   * Emits the "markAsNoShow" event.
   */
  onMarkAsNoShow(event: MouseEvent, eventApi: EventImpl): void {
    event.preventDefault();
    event.stopPropagation();

    this.markAsNoShowEvent.emit(eventApi);
  }
}
